import { _, _any } from "i18n";
import { Keys } from "i18n/locales/types";
import { EyeballApp } from "../../types";
import {
	ColumnEnum,
	ApiPlayerSearchResponse,
	Statistics,
	FilterKeys,
	TQuintiles,
	PlayerPosition,
	SearchFilter,
} from "../types";
import Quintiles from "../static-data/quintiles_beta_version.json";
import QuintilesFemale from "../static-data/quintiles_beta_version.json";
import {
	BIRTHDAY_MAX_YEAR,
	BIRTHDAY_MIN_YEAR,
	DEFAULT_RETURN_VALUE,
	GRID_COLUMNS_KEYS,
	IMAGES,
	PERCENTAGE_STAT_KEYS,
	STATS_FILTERS,
} from "./constants";

import { getInitStatsFilters, getRange, getSelectedSet, getSet } from "./filters";
import { useStatsFiltersStore } from "../stats-filters-store";
import { CONTINENT_COUNTRIES, COUNTRIES_TRANSLATION, CONTINENT_TRANSLATIONS_KEYS, CONTINENT_VALUES } from "../../nationalities/constants";
import { ContinentCodes } from "../../nationalities/types";
import { GenderEnum } from "../../gamePage/types";
import { Gender } from "ui-kit";
import { shouldStripPlayerWeightFromSearchPayload } from "./weightVisibility";

export const getQuintiles = (type?: string, gender?: string) => {
	const quintiles = Quintiles as Statistics;
	const quintilesFemale = QuintilesFemale as Statistics;
	const source = gender === GenderEnum.Female ? quintilesFemale : quintiles;
	const resultMap = new Map<string, { position: string; percentile: number; value: number }[]>();

	for (const key in source.statistics) {
		const stats = source.statistics[key];
		if (type && type !== "ALL") {
			const filtered = stats.filter((item) => item.position === type);
			resultMap.set(key, filtered.length > 0 ? filtered : stats);
		} else {
			resultMap.set(key, stats);
		}
	}
	return resultMap;
};

export const mathRound = (value: number): string => {
	if (value == 0) {
		return "0";
	}

	if (value < 2) {
		return (Math.round(value * 100) / 100).toFixed(2);
	} else if (value < 20) {
		return (Math.round(value * 10) / 10).toFixed(1);
	} else if (value) {
		return Math.round(value).toFixed(0);
	}

	return DEFAULT_RETURN_VALUE;
};

export function assetUrl(name: string): string {
	const index = Object.keys(IMAGES).find((path: string) => path.endsWith(`/${name}.png`));
	if (index) {
		return IMAGES[index];
	}
	return "";
}

export function translatePosition(position: string): string {
	if (position === "") {
		return "";
	}
	return _(`enum.position.${position}.key` as unknown as keyof Keys);
}

export function getPosition(value = "", positionCount = 1): string {
	if (value !== "") {
		const values = value.split(", ");
		const translatedValues = values.map(translatePosition);
		const key = translatedValues.join(", ");
		return `${key}${positionCount > 1 ? "*" : ""}`;
	}
	return "-";
}

export function getGUID(): string {
	let d = new Date().getTime();
	const guid = "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
		const r = (d + Math.random() * 16) % 16 | 0;
		d = Math.floor(d / 16);
		return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
	});
	return guid;
}

// Helper to create stat cell object
const createStatCell = (total: number | undefined, success: number | undefined, position: string | undefined, type: ColumnEnum) => ({
	total: total ?? 0,
	...(success !== undefined && { success }),
	position: position ?? "",
	type,
});

// Helper to extract value from nested object
const getValue = (obj: any): number | undefined => obj?.value;

export function mapPlayers(data?: ApiPlayerSearchResponse, selectedPosition?: string) {
	if (!data?._embedded?.items) return [];

	return data._embedded.items.map((player) => {
		const { firstName, lastName, position, rating, ...rest } = player;

		return {
			...rest,
			name: `${firstName} ${lastName}`,
			position: {
				position,
				positionCount: player.positionCount,
			},
			rating: createStatCell(getValue(rating), undefined, selectedPosition, ColumnEnum.rating),
			goalsScored: createStatCell(player.goalsScored, undefined, selectedPosition, ColumnEnum.goalsScored),
			goalsOwn: createStatCell(player.goalsOwn, undefined, selectedPosition, ColumnEnum.goalsOwn),
			assists: createStatCell(getValue(player.assists), undefined, selectedPosition, ColumnEnum.assists),
			shots: createStatCell(getValue(player.shots), undefined, selectedPosition, ColumnEnum.shots),
			shotsOnTarget: createStatCell(getValue(player.shotsOnTarget), undefined, selectedPosition, ColumnEnum.shotsOnTarget),
			shotsInsidePa: createStatCell(getValue(player.shotsInsidePa), undefined, selectedPosition, ColumnEnum.shotsInsidePa),
			shotsOutsidePa: createStatCell(getValue(player.shotsOutsidePa), undefined, selectedPosition, ColumnEnum.shotsOutsidePa),
			takeons: createStatCell(getValue(player.takeons?.total), getValue(player.takeons?.success), selectedPosition, ColumnEnum.takeons),
			tackles: createStatCell(getValue(player.tackles?.total), getValue(player.tackles?.success), selectedPosition, ColumnEnum.tackles),
			aerialDuels: createStatCell(
				getValue(player.aerialDuels?.total),
				getValue(player.aerialDuels?.success),
				selectedPosition,
				ColumnEnum.aerialDuels
			),
			groundDuels: createStatCell(
				getValue(player.groundDuels?.total),
				getValue(player.groundDuels?.success),
				selectedPosition,
				ColumnEnum.groundDuels
			),
			recoveries: createStatCell(getValue(player.recoveries), undefined, selectedPosition, ColumnEnum.recoveries),
			passes: createStatCell(getValue(player.passes?.total), getValue(player.passes?.success), selectedPosition, ColumnEnum.passes),
			passesAccuracy: createStatCell(getValue(player.passesAccuracy), undefined, selectedPosition, ColumnEnum.passesAccuracy),
			keyPasses: createStatCell(getValue(player.keyPasses), undefined, selectedPosition, ColumnEnum.keyPasses),
			passesForward: createStatCell(
				getValue(player.passesForward?.total),
				getValue(player.passesForward?.success),
				selectedPosition,
				ColumnEnum.passesForward
			),
			crosses: createStatCell(getValue(player.crosses?.total), getValue(player.crosses?.success), selectedPosition, ColumnEnum.crosses),
			controlUnderPressure: createStatCell(getValue(player.controlUnderPressure), undefined, selectedPosition, ColumnEnum.controlUnderPressure),
			goalsConceded: createStatCell(getValue(player.goalsConceded), undefined, selectedPosition, ColumnEnum.goalsConceded),
			catches: createStatCell(getValue(player.catches), undefined, selectedPosition, ColumnEnum.catches),
			punches: createStatCell(getValue(player.punches), undefined, selectedPosition, ColumnEnum.punches),
			goalsKicks: createStatCell(
				getValue(player.goalsKicks?.total),
				getValue(player.goalsKicks?.success),
				selectedPosition,
				ColumnEnum.goalsKicks
			),
			aerialClearances: createStatCell(
				getValue(player.aerialClearances?.total),
				getValue(player.aerialClearances?.success),
				selectedPosition,
				ColumnEnum.aerialClearances
			),
		};
	});
}

export function createPlayerLink(id: number, parentApp: EyeballApp): string {
	if (parentApp === EyeballApp.PORTAL) {
		return `/player/${id}`;
	}

	if (parentApp === EyeballApp.DISCOVER) {
		return `/player-profile/${id}`;
	}

	return `/player/${id}`;
}

export const getStrongFoot = (strongFoot: string): string => {
	const mapping: Record<string, string> = { R: "right", L: "left" };
	return mapping[strongFoot] || "";
};

export const getPlayedInNationalTeam = (playedInNationalTeam: string): number | null => {
	const mapping: Record<string, number> = { Yes: 1, No: 0 };
	return mapping[playedInNationalTeam] ?? null;
};

export function arraysEqual(a1: string[] | undefined, a2: string[] | undefined) {
	return JSON.stringify(a1) === JSON.stringify(a2);
}

const isLikelyPercentile = (value: number): boolean => {
	return value >= 0 && value <= 100;
};

/**
 * Convert actual stat value back to percentile by finding the closest match in quintiles
 */
const valueToPercentile = (value: number | null, quintileData: { percentile: number; value: number }[]): number => {
	if (value === null || value === undefined) {
		return 100;
	}

	if (!quintileData || quintileData.length === 0) {
		// No quintile data available
		// If value looks like a percentile (0-100), use it directly
		// Otherwise, it's likely a stat value we can't convert - use default
		if (isLikelyPercentile(value)) {
			return Math.max(0, Math.min(100, value));
		}
		// Value looks like a stat value but we can't convert it - return default
		// This prevents showing invalid values like 7.039... as percentiles
		return 100;
	}

	const sorted = [...quintileData].sort((a, b) => a.value - b.value);
	const minValue = sorted[0].value;
	const maxValue = sorted[sorted.length - 1].value;

	// Handle edge cases
	if (value === 0 || value < minValue) return 0;

	// Check for exact match (with small tolerance for floating point)
	const exactMatch = sorted.find((item) => item.percentile === value);

	if (exactMatch) return exactMatch.percentile;

	// Linear interpolation between percentiles
	for (let i = 0; i < sorted.length - 1; i++) {
		const lower = sorted[i];
		const upper = sorted[i + 1];

		if (value >= lower.value && value <= upper.value) {
			const range = upper.value - lower.value;
			if (range === 0) return lower.percentile; // Avoid division by zero

			const position = (value - lower.value) / range;
			const percentileRange = upper.percentile - lower.percentile;
			const result = lower.percentile + position * percentileRange;
			return Math.round(Math.max(0, Math.min(100, result))); // Clamp to valid range
		}
	}

	// Fallback: find closest percentile
	const closest = sorted.reduce((closest, item) => {
		const diff = Math.abs(item.value - value);
		return diff < Math.abs(closest.value - value) ? item : closest;
	}, sorted[0]);

	return Math.max(0, Math.min(100, closest.percentile)); // Clamp to valid range
};

/** Get quintile value at a given percentile (used by convertToQuintiles; exported for tests). */
export const percentileToValue = (percentile: number, quintileData: { percentile: number; value: number }[]): number => {
	return quintileData.find((item) => item.percentile === percentile)?.value ?? 0;
};

const DEFAULT_FILTER_VALUES: Record<string, [number, number]> = {
	[FilterKeys.MINUTES]: [90, 900],
	[FilterKeys.GAMES]: [1, 100],
	[FilterKeys.GOALS]: [0, 100],
};

const getDefaultFilterValue = (key: string): [number, number] => {
	return DEFAULT_FILTER_VALUES[key] || [0, 100];
};

const TOTAL_GOALS_ASSISTS_REVERSE_MAP: Record<number | string, number> = {
	0: 0,
	1: 20,
	5: 40,
	10: 60,
	20: 80,
	MAX: 100,
};

/** Percentile values the slider uses; when API returns these, use as-is (preset was saved as percentiles). */
const TOTAL_GOALS_ASSISTS_PERCENTILES = new Set(Object.values(TOTAL_GOALS_ASSISTS_REVERSE_MAP));

/** For restore: null lower bound = min (0), null upper bound = max (100). */
const totalGoalsAssistsValueToPercentile = (value: number | string | null, bound: "lower" | "upper"): number => {
	if (value === null || value === undefined) {
		return bound === "lower" ? 0 : 100;
	}
	return TOTAL_GOALS_ASSISTS_REVERSE_MAP[value] ?? (bound === "lower" ? 0 : 100);
};

const extractFilterValues = (filterItem: any): [number | null, number | null] => {
	const lowerValue = filterItem.gte ?? null;
	const upperValue = filterItem.lte ?? filterItem.lt ?? null;
	return [lowerValue, upperValue];
};

const handleMinutesFilter = (lowerValue: number | null, upperValue: number | null): [number, number] => {
	return [lowerValue ?? 90, upperValue ?? 900];
};

const handleGamesFilter = (lowerValue: number | null, upperValue: number | null): [number, number] => {
	return [lowerValue ?? 1, upperValue ?? 100];
};

const handleTotalGoalsAssistsFilter = (lowerValue: number | string | null, upperValue: number | string | null): [number, number] => {
	// Preset may be saved as percentiles (0, 20, 40, 60, 80, 100); use as-is so round-trip is correct.
	if (
		lowerValue != null &&
		upperValue != null &&
		TOTAL_GOALS_ASSISTS_PERCENTILES.has(Number(lowerValue)) &&
		TOTAL_GOALS_ASSISTS_PERCENTILES.has(Number(upperValue))
	) {
		return [Number(lowerValue), Number(upperValue)];
	}
	const lowerPercentile = totalGoalsAssistsValueToPercentile(lowerValue, "lower");
	const upperPercentile = totalGoalsAssistsValueToPercentile(upperValue, "upper");
	return [lowerPercentile, upperPercentile];
};

const handleGoalsOwnFilter = (lowerValue: number | null, upperValue: number | null): [number, number] => {
	return [lowerValue ?? 0, upperValue ?? 100];
};

const handleQuintileFilter = (
	filterKey: string,
	lowerValue: number | null,
	upperValue: number | null,
	quintileData: { percentile: number; value: number }[]
): [number, number] => {
	if (!quintileData || quintileData.length === 0) {
		const lower = lowerValue !== null && isLikelyPercentile(lowerValue) ? lowerValue : 0;
		const upper = upperValue !== null && isLikelyPercentile(upperValue) ? upperValue : 100;
		return [lower, upper];
	}

	const lowerPercentile = lowerValue !== null ? valueToPercentile(lowerValue, quintileData) : 0;
	const upperPercentile = upperValue !== null ? valueToPercentile(upperValue, quintileData) : 100;

	return [lowerPercentile, upperPercentile];
};

const processFilter = (filterKey: string, filterItem: any, quintiles?: TQuintiles): [number, number] => {
	const [lowerValue, upperValue] = extractFilterValues(filterItem);

	if (filterKey === FilterKeys.MINUTES) {
		return handleMinutesFilter(lowerValue, upperValue);
	}

	if (filterKey === FilterKeys.GAMES) {
		return handleGamesFilter(lowerValue, upperValue);
	}

	if (filterKey === FilterKeys.TOTAL_GOALS || filterKey === FilterKeys.TOTAL_ASSISTS) {
		return handleTotalGoalsAssistsFilter(lowerValue, upperValue);
	}

	if (filterKey === FilterKeys.GOALS_OWN) {
		return handleGoalsOwnFilter(lowerValue, upperValue);
	}

	if (quintiles && quintiles instanceof Map && quintiles.has(filterKey)) {
		const quintileData = quintiles.get(filterKey)!;

		const requiresTransformFromQuintiles = filterItem.lt === undefined || filterItem.gte === undefined;

		if (requiresTransformFromQuintiles && quintileData && quintileData.length > 0) {
			return handleQuintileFilter(filterKey, lowerValue, upperValue, quintileData);
		}
	}

	const lower = lowerValue !== null && isLikelyPercentile(lowerValue) ? lowerValue : 0;
	const upper = upperValue !== null && isLikelyPercentile(upperValue) ? upperValue : 100;
	return [lower, upper];
};

export const mapApiObjToEntity = (apiData: any, quintiles?: TQuintiles) => {
	const filters: Record<string, [number, number]> = {};

	STATS_FILTERS.forEach((filterKey: string) => {
		const filterItem = apiData?.[filterKey];
		if (filterItem) {
			filters[filterKey] = processFilter(filterKey, filterItem, quintiles);
		} else {
			filters[filterKey] = getDefaultFilterValue(filterKey);
		}
	});

	return filters;
};

export const getPlayerFilterPayload = (player: { [x: string]: any }) => {
	return {
		...(player.playerName && { playerName: player.playerName.value }),
		...(player.clubName && { clubName: [player.clubName.value] }),
		...(player.clubId && { clubId: [player.clubId.value] }),
		...(player.teamId && { teamId: [player.teamId.value] }),
		...(player.teamName?.length && { teamId: player.teamName.map((team: { value: number }) => team.value) }),
		...(player.teamAgeGroup && { teamAgeGroup: [player.teamAgeGroup] }),
		...(player.teamLevel && { teamLevel: [player.teamLevel] }),
		...(player.nationality && { nationality: [player.nationality] }),
		...(player.strongFoot && { strongFoot: player.strongFoot }),
		...(player.position && { position: player.position }),
		...(player.birthday && { birthday: { gte: player.birthday[0], lte: player.birthday[1] } }),
		...(player.height && { height: { gte: player.height[0], lte: player.height[1] } }),
		...(player.weight && { weight: { gte: player.weight[0], lte: player.weight[1] } }),
		...((player.playedInNationalTeam === 0 || player.playedInNationalTeam === 1) && {
			playedInNationalTeam: player.playedInNationalTeam,
		}),
		...(player.gender && { gender: player.gender }),
	};
};

const STATS_RANGE_KEYS = ["gte", "lte", "lt"] as const;
const PERCENTAGE_KEYS_SET = new Set(PERCENTAGE_STAT_KEYS);
/** Epsilon so integers serialize as float in JSON (backend expects float, not int). */
const FLOAT_EPSILON = 0.001;

/** Decimal places for stat filter values to avoid floating-point noise (e.g. 0.8116400000000001). */
const STAT_FILTER_DECIMALS = 3;

/** True if value is in 0–1 (quintile/backend) scale. */
const inZeroOneScale = (v: number): boolean => v >= 0 && v <= 1;

/** Parse to number without adding epsilon (used before quintile conversion and bound omission). */
function parseStatValue(v: unknown): number | null {
	if (v === null || v === undefined) return null;
	const num = Number(v);
	if (Number.isNaN(num)) return null;
	if (num === 0) return 0;
	return Number(parseFloat(num.toFixed(STAT_FILTER_DECIMALS)));
}

/** Apply float epsilon to integers only (0 is left as 0). Left bounds (gte) use minus epsilon so the bound is inclusive. */
function ensureFloat(v: number, isLeftBound?: boolean, hasQuintiles?: boolean): number | null {
	if (v == 0) {
		if (hasQuintiles) {
			return 0;
		}
		return isLeftBound ? -FLOAT_EPSILON : FLOAT_EPSILON;
	}

	const isWhole = v === Math.floor(v) && v === Math.ceil(v);
	const isInt = isWhole || Number.isInteger(v);
	if (!isInt) return v;
	return isLeftBound ? v - FLOAT_EPSILON : v + FLOAT_EPSILON;
}

/** Converts a single raw bound (gte/lte/lt) to the payload value (percentile 0–100 or pass-through). */
function convertToQuintiles(
	raw: number,
	opts: {
		isPercentageStat: boolean;
		quintileData: { percentile: number; value: number }[] | undefined;
	}
): number {
	const { isPercentageStat, quintileData } = opts;
	const hasQuintiles = quintileData && quintileData.length > 0;

	if (hasQuintiles) {
		// Percentage stats may store 0–1 normalized percentile → scale to 0–100.
		// Other stats (e.g. goalsScored) store actual values (0.01, 0.5, 1) → convert via quintiles.
		if (isPercentageStat && inZeroOneScale(raw)) {
			if (raw > 0 && raw < 1) {
				return percentileToValue(Math.round(raw * 100), quintileData!);
			}
			return raw;
		}

		return percentileToValue(raw, quintileData!);
	}

	if (isPercentageStat && inZeroOneScale(raw)) {
		return Math.round(raw * 100);
	}
	return raw;
}

const convertIntegersToFloats = (
	mapped: Record<string, number>,
	quintileData: { percentile: number; value: number }[] | undefined
): Record<string, number> => {
	const withFloats: Record<string, number> = {};
	for (const k of Object.keys(mapped)) {
		const float = ensureFloat(mapped[k], k === "gte", !!quintileData);
		if (float) {
			withFloats[k] = float;
		}
	}
	return withFloats;
};

/**
 * Maps stats filter object to backend payload.
 * - Converts stat values to percentiles (0–100) when quintile data exists.
 * - Omits gte/lte bounds 0 and 100 for percentage stats (full range = no filter).
 */
export const mapStatsToPayload = (stats: Record<string, unknown>, quintiles: TQuintiles): Record<string, unknown> => {
	const result: Record<string, unknown> = {};

	for (const [key, value] of Object.entries(stats)) {
		const range = value as Record<string, unknown>;
		const isPercentageStat = PERCENTAGE_KEYS_SET.has(key);
		const quintileData = quintiles?.get(key);
		const mapped: Record<string, number> = {};

		for (const boundKey of STATS_RANGE_KEYS) {
			if (!(boundKey in range)) {
				continue;
			}

			const raw = parseStatValue(range[boundKey]);

			if (raw === null || raw === undefined) {
				continue;
			}
			if (isPercentageStat && (raw === 0 || raw === 1)) {
				continue;
			}

			if (quintileData) {
				mapped[boundKey] = convertToQuintiles(raw, { isPercentageStat, quintileData });
			} else {
				mapped[boundKey] = raw;
			}
		}

		if (Object.keys(mapped).length > 0) {
			// Ensure integers serialize as float (backend expects float)
			result[key] = convertIntegersToFloats(mapped, quintileData);
		}
	}

	return result;
};

export const createSearchFilterPayload = (
	filter: SearchFilter,
	quintiles: TQuintiles,
	setSelectedPosition: (position: string) => void,
	weightContext?: { parentApp: EyeballApp; profileSelectedGender: Gender | undefined }
) => {
	const newFilter = { ...filter };
	const ranges = useStatsFiltersStore.getState().ranges;
	const statsFromStore = getInitStatsFilters(ranges, quintiles);

	newFilter.stats = Object.keys(statsFromStore).length > 0 ? statsFromStore : newFilter.stats;

	/**
	 * Clean the side bar filters of the bound values or unwanted empty values
	 */

	if (newFilter?.player?.clubName) {
		const cleansedClubName = newFilter.player.clubName.filter(Boolean);
		if (cleansedClubName.length === 0) {
			const { clubName, ...playerWithoutClubName } = newFilter.player;
			newFilter.player = playerWithoutClubName;
		} else {
			newFilter.player.clubName = cleansedClubName;
		}
	}

	if (newFilter?.player?.countryCodes) {
		const cleansedCountryCodes = newFilter.player.countryCodes.filter(Boolean);
		if (cleansedCountryCodes.length === 0) {
			const { countryCodes, ...playerWithoutCountryCodes } = newFilter.player;
			newFilter.player = playerWithoutCountryCodes;
		} else {
			newFilter.player.countryCodes = cleansedCountryCodes;
		}
	}

	if (newFilter?.player?.position && typeof newFilter.player.position !== "string") {
		const playerPosition = newFilter.player.position as { value: string };
		if (playerPosition.value === PlayerPosition.ALL) {
			const { position, ...playerWithoutPosition } = newFilter.player;
			newFilter.player = playerWithoutPosition;
		} else {
			setSelectedPosition(playerPosition.value);
			newFilter.player.position = playerPosition.value;
		}
	}

	if (newFilter?.player?.teamAgeGroup) {
		newFilter.player.teamAgeGroup = newFilter.player.teamAgeGroup.map((data: string | { value: string }) => {
			if (typeof data === "string") return data;
			return data.value;
		});
		if (arraysEqual(newFilter?.player?.teamAgeGroup, ["-"])) {
			const { teamAgeGroup, ...playerWithoutTeamAgeGroup } = newFilter.player;
			newFilter.player = playerWithoutTeamAgeGroup;
		}
	}

	if (newFilter?.player?.teamLevel) {
		newFilter.player.teamLevel = newFilter.player.teamLevel.map((data: string | { value: string }) => {
			if (typeof data === "string") return data;
			return data.value;
		});
		if (arraysEqual(newFilter?.player?.teamLevel, ["-"])) {
			const { teamLevel, ...playerWithoutTeamLevel } = newFilter.player;
			newFilter.player = playerWithoutTeamLevel;
		}
	}

	if (newFilter?.player?.nationality) {
		newFilter.player.nationality = newFilter.player.nationality.map((data: string | { value: string }) => {
			if (typeof data === "string") return data;
			return data.value;
		});
		if (arraysEqual(newFilter?.player?.nationality, ["-"])) {
			const { nationality, ...playerWithoutNationality } = newFilter.player;
			newFilter.player = playerWithoutNationality;
		}
	}

	if (newFilter?.player?.birthday) {
		if (newFilter.player.birthday.lte === BIRTHDAY_MAX_YEAR && newFilter.player.birthday.gte === BIRTHDAY_MIN_YEAR) {
			const { birthday, ...playerWithoutBirthday } = newFilter.player;
			newFilter.player = playerWithoutBirthday;
		}
	}

	if (newFilter?.player?.height) {
		if (newFilter.player.height.lte === 220 && newFilter.player.height.gte === 60) {
			const { height, ...playerWithoutHeight } = newFilter.player;
			newFilter.player = playerWithoutHeight;
		}
	}

	if (newFilter?.player?.weight) {
		if (newFilter.player.weight.lte === 120 && newFilter.player.weight.gte === 30) {
			const { weight, ...playerWithoutWeight } = newFilter.player;
			newFilter.player = playerWithoutWeight;
		}
	}

	if (
		weightContext &&
		newFilter?.player &&
		shouldStripPlayerWeightFromSearchPayload({
			parentApp: weightContext.parentApp,
			profileSelectedGender: weightContext.profileSelectedGender,
			player: newFilter.player,
		})
	) {
		const { weight, ...playerWithoutWeight } = newFilter.player;
		newFilter.player = playerWithoutWeight;
	}

	// Clean stats: drop birthday/height/weight from stats, then remap all gte/lte to float (backend expects float; omit null/zero).
	const { birthday, height, weight, ...statsOnly } = newFilter.stats as Record<string, unknown>;
	newFilter.stats = mapStatsToPayload(statsOnly, quintiles) as typeof newFilter.stats;

	return newFilter;
};

const isDefaultFilterValue = (key: string, value: [number, number]): boolean => {
	const defaults = DEFAULT_FILTER_VALUES[key] || [0, 100];
	return value[0] === defaults[0] && value[1] === defaults[1];
};

export const getStatsFilterPayload = (obj: { [x: string]: any[] }) => {
	const submitObj: Record<string, { gte: number; lt: number | null }> = {};

	STATS_FILTERS.forEach((key: string) => {
		const value = obj[key];
		if (!value || !Array.isArray(value) || value.length < 2) {
			return;
		}

		if (isDefaultFilterValue(key, value as [number, number])) {
			return;
		}

		submitObj[key] = {
			gte: value[0],
			lt: value[1] === 100 ? null : value[1],
		};
	});

	return submitObj;
};

export const getStatsFilterPayloadSearch = (
	obj: { [key: string]: any },
	quintiles: TQuintiles
): { statsFilter: { [key: string]: any }; submitObj: { [key: string]: any } } => {
	const statsFilter: Record<string, any> = {};
	const submitObj: Record<string, { gte: number; lt: number | null }> = {};

	STATS_FILTERS.forEach((key: string) => {
		const value = obj[key];
		if (!value || !Array.isArray(value) || value.length < 2) {
			return;
		}

		const percentileRange = value as [number, number];
		if (isDefaultFilterValue(key, percentileRange)) {
			return;
		}

		// Ensure [min, max] order so gte/lte are correct (slider or storage may provide reversed order)
		const from = Math.min(percentileRange[0], percentileRange[1]);
		const to = Math.max(percentileRange[0], percentileRange[1]);
		const range = getRange(key, from, to, quintiles);
		if (range) {
			// Normalize 'lt' to 'lte'
			const normalized = "lt" in range && !("lte" in range) ? { ...range, lt: (range as any).lt, lte: undefined } : range;
			statsFilter[key] = normalized;
		}

		submitObj[key] = {
			gte: percentileRange[0],
			lt: percentileRange[1] === 100 ? null : percentileRange[1],
		};
	});

	return { statsFilter, submitObj };
};

/**
 * @param columnKeys - Defaults to all grid keys; pass `getPlayerGridColumnKeysForPlayersGrid(...)` when `weight` is not part of the grid.
 */
export const getInitialHiddenColumns = (columnKeys: ColumnEnum[] = GRID_COLUMNS_KEYS): string[] => {
	const selectedColumns = getSet(getSelectedSet());
	if (selectedColumns) {
		return columnKeys.filter((item) => !selectedColumns.includes(item));
	}
	return [];
};

export const getNationalitiesOptions = () => {
	const continents = Object.keys(CONTINENT_COUNTRIES) as ContinentCodes[];

	const groupedCountries = continents.map((continentCode) => {
		const options = CONTINENT_COUNTRIES[continentCode]
			.map((countryCode) => ({ value: countryCode, label: COUNTRIES_TRANSLATION[countryCode] }))
			.sort((a, b) => a.label.localeCompare(b.label));

		return {
			label: _any(CONTINENT_TRANSLATIONS_KEYS[continentCode]),
			options,
		};
	});

	const continentOptions = continents.map((continentCode) => ({
		value: CONTINENT_VALUES[continentCode],
		label: _any(CONTINENT_TRANSLATIONS_KEYS[continentCode]),
	}));

	return [
		{
			label: "",
			options: [
				{
					value: "-",
					label: "-",
				},
			],
		},
		{
			label: _("label.nationality.continents"),
			options: continentOptions,
		},
		...groupedCountries,
	];
};
